import type { I18nText } from "./locales";
import { MERGE_DESC_MAX_WIDTH, descWidth } from "../../../src/game/merge-desc-rules.ts";
import type { OrderConfigDocument } from "../../../src/game/merge/shared-orders/types.ts";

// 订单配置文档（规格 v0.23）：形状和共享引擎一致，默认起点与抽样门槛和服务端同一份（src/game/orders-config/defaults.ts，不带 zod 和引擎）
export { DEFAULT_ORDER_CONFIG, MAX_ITEMS_PER_ORDER, MAX_LANE_SLOTS, REACHABILITY_MIN_HIT_RATE, REACHABILITY_WARN_HIT_RATE } from "../../../src/game/orders-config/defaults.ts";
export type MergeOrdersDoc = OrderConfigDocument;
export type MergeOrdersIssue = { path: string; message: string };
/** POST /config/versions/:v/merge/orders/preview 的返回（src/game/orders-config/publish.ts OrderConfigPreview） */
export type MergeOrdersPreview = {
  structuralProblems: MergeOrdersIssue[];
  catalogProblems: MergeOrdersIssue[];
  problems: Array<MergeOrdersIssue & { code: string }>;
  poolCount: number;
  reachability: Array<{ laneId: MergeOrdersDoc["lanes"][number]["laneId"]; hitRate: number }>;
  cleanupPool: Array<{ itemCode: string; amount: number; difficulty: number; reward: number }>;
  cleanupMaxReward: number;
};

// 物品简介长度口径与服务端发布闸共用一份（src/game/merge-desc-rules.ts），这里只转出
export { MERGE_DESC_MAX_WIDTH, descWidth, descWidthIssues } from "../../../src/game/merge-desc-rules.ts";
/** 物品对话框简介框的长度上限：每种语言按 trim 后的显示宽度（全角算 2）≤ 30 */
export const MERGE_DESC_LIMIT = { max: MERGE_DESC_MAX_WIDTH, measure: descWidth };

/**
 * 合成与订单的配置块（版本内，整块读写 `/config/versions/:v/merge`）。
 * 行形状与服务端 src/game/merge-config-rows.ts 一致（camelCase）；chainId / level 不落库，
 * 服务端读时按合成关系推导、这里改合成关系后也当场重算（deriveChains），两边同一规则。
 * 产出权重：接口与库存的是已 ×100 的整数，页面负责两位小数 ↔ ×100 的换算（weightToText / weightFromText）。
 */
export type ProduceKind = "unlimited" | "cooldown" | "limited";
export type MergeProduce = {
  kind: ProduceKind; energyCost: number; capacity: number | null; cooldownMs: number | null;
  multiplierEnabled: boolean; drops: Array<{ itemDefId: string; weight: number }>;
};
export type CollectKind = "energy" | "fragment" | "gems";
/** 图鉴解锁奖励的种类（规格 v0.25 3.1）：只给体力或钻石，不给碎片 */
export type DexRewardKind = "energy" | "gems";
export type MergeItem = {
  itemId: string; nameI18n: I18nText;
  /** 简介（客户端底栏信息卡显示），可不填，填了就得有 en，每种语言显示宽度 ≤ 30（MERGE_DESC_LIMIT）；服务端没存就不带键，这里统一成 null */
  descI18n: I18nText | null;
  icon: string; mergeResultId: string | null;
  produce: MergeProduce | null; collectKind: CollectKind | null; collectAmount: number | null; sellFragment: number | null;
  /** 订单引擎的两个字段（v0.23）。null = 没填、按默认（可下单：链首不可、其余可；难度 2^(等级−1)）；服务端没存就不带键，这里统一成 null */
  orderable: boolean | null;
  difficulty: number | null;
  /**
   * 图鉴（v0.25）：解锁奖励的种类与数量，两个同为 null（没有奖励）或同有值——只给一个服务端整块 400，所以页面上从不只留一个。
   * 数量填错（空、0）时存 0，发布闸报 items[<id>].dexReward.amount；服务端没存就不带键，这里统一成 null
   */
  dexRewardKind: DexRewardKind | null;
  dexRewardAmount: number | null;
  /** 图鉴卡片标题（系列名），只在链首上有意义；没配回退链首物品名。服务端没存就不带键，这里统一成 null */
  seriesNameI18n: I18nText | null;
};
// 棋盘固定 7 列 × 9 行（与共享规则 src/game/merge/types.ts 的 BOARD_* 同值），格数不是配置项
export const BOARD_COLUMNS = 7;
export const BOARD_ROWS = 9;
export const BOARD_SIZE = BOARD_COLUMNS * BOARD_ROWS;
/**
 * 棋盘解锁（设计 v0.4）：`hiddenSlots` 是开局盖着的格（升序，页面上一律给数组，服务端空了就不存）；
 * 初始物品 `locked: true` 是开放格上开局就锁定的，不锁就不带键。盖着的格里的物品一律锁定，不看 locked
 */
export type MergeBoard = {
  orderSlotCount: number;
  initialItems: Array<{ itemDefId: string; slot: number; locked?: true }>;
  hiddenSlots: number[];
};
export type MergeNpc = { npcId: string; nameI18n: I18nText; avatar: string };
export type MergeOrderTemplate = {
  templateId: string; npcId: string; enabled: boolean; minIntimacyLevel: number; maxIntimacyLevel: number | null;
  requirements: Array<{ itemDefId: string; quantity: number }>; fragment: number; selectionWeight: number; refillDelayMs: number;
};
export type MergeEnergy = { initialAmount: number; naturalCap: number; intervalMs: number };
export type MergeMultiplier = { maxMultiplier: number; unlocks: Array<{ multiplier: number; intimacyLevel: number }> };
export type MergeIntimacyReward = { ruleId: string; thresholdLevel: number; items: Array<{ itemDefId: string; quantity: number }> };
/** 仓库（规格 6.5）：所有棋盘共用一份；slotPrices[i] 是免费格之后第 i+1 格的钻石价 */
export type MergeWarehouse = { unlockIntimacyLevel: number; initialSlots: number; slotPrices: number[] };
/**
 * 生成器仓库（规格 v0.26 6.6）：所有棋盘共用一份，和仓库分开配。每格绑一条生成器链（填链首 id），
 * 只收这条链的链顶生成器；数组顺序就是游戏里面板的顺序
 */
export type MergeProducerWarehouse = { unlockIntimacyLevel: number; slots: Array<{ chainId: string; unlockIntimacyLevel: number }> };
/**
 * 图鉴分组：底部一个页签一组。数组顺序就是页签顺序，chainIds（链首物品 id）的顺序就是组里卡片的顺序。
 * 名字只给管理端认组用，玩家看不到；空串 = 没起名（服务端落 NULL）
 */
export type MergeDexGroup = { groupId: string; icon: string; name: string; chainIds: string[] };

export type MergeBlock = {
  items: MergeItem[];
  board: MergeBoard | null;
  npcs: MergeNpc[];
  orderTemplates: MergeOrderTemplate[];
  energy: MergeEnergy | null;
  multiplier: MergeMultiplier | null;
  intimacyRewards: MergeIntimacyReward[];
  /** null = 这一版没有仓库 */
  warehouse: MergeWarehouse | null;
  /** null = 这一版没有生成器仓库 */
  producerWarehouse: MergeProducerWarehouse | null;
  /** 图鉴分组：空数组 = 这一版不分组（图鉴一张列表、没有页签） */
  dexGroups: MergeDexGroup[];
  /** 订单配置文档（v0.23）：null = 这一版还没配，发布闸会拦 */
  orders: MergeOrdersDoc | null;
};

export const EMPTY_MERGE: MergeBlock = { items: [], board: null, npcs: [], orderTemplates: [], energy: null, multiplier: null, intimacyRewards: [], warehouse: null, producerWarehouse: null, dexGroups: [], orders: null };

/** 十一张全空 = 这一版不配合成玩法（服务端认为合法） */
export function mergeBlockEmpty(b: MergeBlock): boolean {
  return !b.items.length && !b.board && !b.npcs.length && !b.orderTemplates.length && !b.energy && !b.multiplier && !b.intimacyRewards.length && !b.warehouse && !b.producerWarehouse && !b.dexGroups.length && !b.orders;
}

export const DEFAULT_BOARD: MergeBoard = { orderSlotCount: 3, initialItems: [], hiddenSlots: [] };
export const DEFAULT_ENERGY: MergeEnergy = { initialAmount: 30, naturalCap: 30, intervalMs: 180_000 };
export const DEFAULT_MULTIPLIER: MergeMultiplier = { maxMultiplier: 1, unlocks: [] };
export const DEFAULT_WAREHOUSE: MergeWarehouse = { unlockIntimacyLevel: 0, initialSlots: 5, slotPrices: [] };
export const DEFAULT_PRODUCER_WAREHOUSE: MergeProducerWarehouse = { unlockIntimacyLevel: 0, slots: [] };
/** 一块棋盘的仓库最多几格（免费格 + 付费格），与 src/game/merge-validate.ts 的 WAREHOUSE_MAX_SLOTS 同值 */
export const WAREHOUSE_MAX_SLOTS = 99;
export const MULTIPLIER_CHOICES = [1, 2, 4, 8, 16, 32] as const;
export const PRODUCE_KINDS: readonly ProduceKind[] = ["unlimited", "cooldown", "limited"];
export const COLLECT_KINDS: readonly CollectKind[] = ["energy", "fragment", "gems"];
export const DEX_REWARD_KINDS: readonly DexRewardKind[] = ["energy", "gems"];
/** 每列产出总权重上限（规格 3.1，已 ×100 的整数） */
export const DROP_WEIGHT_TOTAL_MAX = 65_536;

// ── 棋盘初始格：普通 / 遮住 / 锁定 ──
// 页面上每格一个状态；存的照旧是 hiddenSlots（遮住）和初始物品的 locked（开放格上锁定）两样，
// 状态由这两样推出，改状态也只改这两样，块的形状、服务端、客户端都不动。
// 一格只有一个状态：盖着的格不再带 locked（盖着本来就锁定），这样揭开一格不会冒出旧的锁定。

export type BoardCellState = "normal" | "cover" | "lock";
export const BOARD_CELL_STATES: readonly BoardCellState[] = ["normal", "cover", "lock"];
type BoardItem = MergeBoard["initialItems"][number];

/** 遮住优先；开放格上带 locked 的物品是锁定；其余是普通（空格也是普通） */
export function boardCellState(b: MergeBoard, slot: number): BoardCellState {
  if (b.hiddenSlots.includes(slot)) return "cover";
  return b.initialItems.some((x) => x.slot === slot && x.locked) ? "lock" : "normal";
}

const unlockItem = ({ itemDefId, slot }: BoardItem): BoardItem => ({ itemDefId, slot });
const itemAt = (itemDefId: string, slot: number, locked: boolean): BoardItem => (locked ? { itemDefId, slot, locked: true } : { itemDefId, slot });
const bySlot = (x: BoardItem, y: BoardItem) => x.slot - y.slot;

/** 改一格的状态。锁定要格里有物品：空格设锁定原样返回 */
export function setBoardCellState(b: MergeBoard, slot: number, state: BoardCellState): MergeBoard {
  if (state === "lock" && !b.initialItems.some((y) => y.slot === slot)) return b;
  const rest = b.hiddenSlots.filter((s) => s !== slot);
  return {
    ...b,
    hiddenSlots: state === "cover" ? [...rest, slot].sort((x, y) => x - y) : rest,
    initialItems: b.initialItems.map((y) => (y.slot !== slot ? y : itemAt(y.itemDefId, slot, state === "lock"))),
  };
}

/** 从候选区拖进一格：原有的物品被换掉，格的状态不变（锁定格换了物品还是锁定） */
export function placeBoardItem(b: MergeBoard, slot: number, itemDefId: string): MergeBoard {
  const locked = boardCellState(b, slot) === "lock";
  return { ...b, initialItems: [...b.initialItems.filter((y) => y.slot !== slot), itemAt(itemDefId, slot, locked)].sort(bySlot) };
}

/** 拿掉一格的物品。锁定跟着物品走，所以锁定格变回普通；遮住的格照旧遮住（盖着的空格是合法的） */
export function removeBoardItem(b: MergeBoard, slot: number): MergeBoard {
  if (!b.initialItems.some((y) => y.slot === slot)) return b;
  return { ...b, initialItems: b.initialItems.filter((y) => y.slot !== slot) };
}

/**
 * 棋盘上把一格的物品拖到另一格：目标格空就挪过去，有物品就两格互换。
 * 状态留在格上不跟物品走：两格各自的遮住、锁定照旧，只是源格挪空了，原来锁定的变回普通
 */
export function moveBoardItem(b: MergeBoard, from: number, to: number): MergeBoard {
  const a = b.initialItems.find((y) => y.slot === from);
  if (!a || from === to) return b;
  const c = b.initialItems.find((y) => y.slot === to);
  const moved = [itemAt(a.itemDefId, to, boardCellState(b, to) === "lock")];
  if (c) moved.push(itemAt(c.itemDefId, from, boardCellState(b, from) === "lock"));
  return { ...b, initialItems: [...b.initialItems.filter((y) => y.slot !== from && y.slot !== to), ...moved].sort(bySlot) };
}

/** 全部遮住 / 全部揭开：遮住时去掉所有 locked；揭开只把原来盖着的格变普通，开放格上的锁定不动 */
export function coverWholeBoard(b: MergeBoard, on: boolean): MergeBoard {
  if (on) return { ...b, hiddenSlots: Array.from({ length: BOARD_SIZE }, (_, i) => i), initialItems: b.initialItems.map(unlockItem) };
  const hidden = new Set(b.hiddenSlots);
  return { ...b, hiddenSlots: [], initialItems: b.initialItems.map((y) => (hidden.has(y.slot) ? unlockItem(y) : y)) };
}

type Raw = Record<string, unknown>;
const isObj = (v: unknown): v is Raw => v != null && typeof v === "object" && !Array.isArray(v);
const num = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : d);
const numOrNull = (v: unknown) => (v == null || v === "" ? null : num(v));
const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const i18n = (v: unknown): I18nText => (isObj(v) ? Object.fromEntries(Object.entries(v).filter((e): e is [string, string] => typeof e[1] === "string")) : {});
const idQty = (v: unknown) => (Array.isArray(v) ? v.filter(isObj).map((x) => ({ itemDefId: str(x.itemDefId), quantity: num(x.quantity) })) : []);

function produceOf(v: unknown): MergeProduce | null {
  if (!isObj(v)) return null;
  const kind = PRODUCE_KINDS.includes(v.kind as ProduceKind) ? (v.kind as ProduceKind) : "unlimited";
  return {
    kind, energyCost: num(v.energyCost), capacity: numOrNull(v.capacity), cooldownMs: numOrNull(v.cooldownMs),
    multiplierEnabled: v.multiplierEnabled === true || Number(v.multiplierEnabled) === 1,
    drops: Array.isArray(v.drops) ? v.drops.filter(isObj).map((d) => ({ itemDefId: str(d.itemDefId), weight: num(d.weight) })) : [],
  };
}

/**
 * 图鉴奖励两列成对读：种类不认识就两个一起当没配，免得留下「没种类、有数量」这一半，下次整块保存被服务端 400。
 * 只是兜底：写接口已拒收 energy / gems 以外的种类（同 collectKind），库里不该有页面认不出的种类
 */
function dexRewardOf(x: Raw): Pick<MergeItem, "dexRewardKind" | "dexRewardAmount"> {
  const kind = DEX_REWARD_KINDS.includes(x.dexRewardKind as DexRewardKind) ? (x.dexRewardKind as DexRewardKind) : null;
  return kind ? { dexRewardKind: kind, dexRewardAmount: num(x.dexRewardAmount) } : { dexRewardKind: null, dexRewardAmount: null };
}

/** 服务端 GET 的块 → 页面状态。不认识的字段丢掉，缺的补默认；单例块缺行是 null（这一版没配） */
export function toMergeBlock(r: Raw): MergeBlock {
  const items = Array.isArray(r.items) ? r.items.filter(isObj).map((x): MergeItem => ({
    itemId: str(x.itemId), nameI18n: i18n(x.nameI18n), descI18n: x.descI18n == null ? null : i18n(x.descI18n), icon: str(x.icon), mergeResultId: x.mergeResultId == null || x.mergeResultId === "" ? null : str(x.mergeResultId),
    produce: produceOf(x.produce), collectKind: COLLECT_KINDS.includes(x.collectKind as CollectKind) ? (x.collectKind as CollectKind) : null,
    collectAmount: numOrNull(x.collectAmount), sellFragment: numOrNull(x.sellFragment),
    orderable: x.orderable == null ? null : x.orderable === true || Number(x.orderable) === 1,
    difficulty: numOrNull(x.difficulty),
    ...dexRewardOf(x),
    seriesNameI18n: x.seriesNameI18n == null ? null : i18n(x.seriesNameI18n),
  })) : [];
  const board: MergeBoard | null = isObj(r.board) ? {
    orderSlotCount: num(r.board.orderSlotCount),
    initialItems: Array.isArray(r.board.initialItems)
      ? r.board.initialItems.filter(isObj).map((x) => ({ itemDefId: str(x.itemDefId), slot: num(x.slot), ...(x.locked === true ? { locked: true as const } : {}) }))
      : [],
    hiddenSlots: Array.isArray(r.board.hiddenSlots) ? [...new Set(r.board.hiddenSlots.map((s) => num(s)))].sort((a, b) => a - b) : [],
  } : null;
  const npcs = Array.isArray(r.npcs) ? r.npcs.filter(isObj).map((x): MergeNpc => ({ npcId: str(x.npcId), nameI18n: i18n(x.nameI18n), avatar: str(x.avatar) })) : [];
  const orderTemplates = Array.isArray(r.orderTemplates) ? r.orderTemplates.filter(isObj).map((x): MergeOrderTemplate => ({
    templateId: str(x.templateId), npcId: str(x.npcId), enabled: x.enabled === true || Number(x.enabled) === 1,
    minIntimacyLevel: num(x.minIntimacyLevel), maxIntimacyLevel: numOrNull(x.maxIntimacyLevel), requirements: idQty(x.requirements),
    fragment: num(x.fragment), selectionWeight: num(x.selectionWeight, 1), refillDelayMs: num(x.refillDelayMs),
  })) : [];
  const energy = isObj(r.energy) ? { initialAmount: num(r.energy.initialAmount), naturalCap: num(r.energy.naturalCap), intervalMs: num(r.energy.intervalMs) } : null;
  const multiplier = isObj(r.multiplier) ? { maxMultiplier: num(r.multiplier.maxMultiplier, 1), unlocks: Array.isArray(r.multiplier.unlocks) ? r.multiplier.unlocks.filter(isObj).map((u) => ({ multiplier: num(u.multiplier), intimacyLevel: num(u.intimacyLevel) })) : [] } : null;
  const intimacyRewards = Array.isArray(r.intimacyRewards) ? r.intimacyRewards.filter(isObj).map((x): MergeIntimacyReward => ({ ruleId: str(x.ruleId), thresholdLevel: num(x.thresholdLevel), items: idQty(x.items) })) : [];
  const warehouse = isObj(r.warehouse) ? {
    unlockIntimacyLevel: num(r.warehouse.unlockIntimacyLevel), initialSlots: num(r.warehouse.initialSlots),
    slotPrices: Array.isArray(r.warehouse.slotPrices) ? r.warehouse.slotPrices.map((p) => num(p)) : [],
  } : null;
  const producerWarehouse = isObj(r.producerWarehouse) ? {
    unlockIntimacyLevel: num(r.producerWarehouse.unlockIntimacyLevel),
    slots: Array.isArray(r.producerWarehouse.slots) ? r.producerWarehouse.slots.filter(isObj).map((x) => ({ chainId: str(x.chainId), unlockIntimacyLevel: num(x.unlockIntimacyLevel) })) : [],
  } : null;
  // 图鉴分组：老服务端没这个键就是不分组；名字没起是空串
  const dexGroups = Array.isArray(r.dexGroups) ? r.dexGroups.filter(isObj).map((g): MergeDexGroup => ({
    groupId: str(g.groupId), icon: str(g.icon), name: str(g.name),
    chainIds: Array.isArray(g.chainIds) ? g.chainIds.map(str) : [],
  })) : [];
  // 订单配置文档原样接过来：形状由服务端 zod 判，页面只要它是个对象
  const orders = isObj(r.orders) ? (r.orders as unknown as MergeOrdersDoc) : null;
  return { items, board, npcs, orderTemplates, energy, multiplier, intimacyRewards, warehouse, producerWarehouse, dexGroups, orders };
}

const filledI18n = (v: I18nText | null): v is I18nText => !!v && Object.values(v).some((s) => s.trim());

/** 页面状态 → PUT 体（enabled 转 0/1；物品简介一种语言都没填就不发；系列名一种语言都没填发 null；其余同形） */
export function toMergePutBody(b: MergeBlock) {
  return {
    items: b.items.map(({ descI18n, ...it }) => {
      const out = { ...it, seriesNameI18n: filledI18n(it.seriesNameI18n) ? it.seriesNameI18n : null };
      return filledI18n(descI18n) ? { ...out, descI18n } : out;
    }),
    board: b.board, npcs: b.npcs,
    orderTemplates: b.orderTemplates.map((t) => ({ ...t, enabled: t.enabled ? 1 : 0 })),
    // 单例块 null 也要带上、分组空数组也要带上：服务端缺键整块 400（防止哪一页忘了带、静默清空）
    energy: b.energy, multiplier: b.multiplier, intimacyRewards: b.intimacyRewards, warehouse: b.warehouse, producerWarehouse: b.producerWarehouse,
    dexGroups: b.dexGroups, orders: b.orders,
  };
}

export type ChainInfo = { chainId: string; level: number };

/**
 * 按合成关系推导链头与等级（规格 3.1）：链头 = 没有别的物品合成到它的物品；等级 = 从链头走到它的步数 + 1。
 * 配置里有环或多个来源时，服务端发布校验会报；这里只保证不死循环、每个物品都有个显示值。
 */
export function deriveChains(items: readonly MergeItem[]): Map<string, ChainInfo> {
  const byId = new Map(items.map((it) => [it.itemId, it]));
  const sourceOf = new Map<string, string>();
  for (const it of items) if (it.mergeResultId && byId.has(it.mergeResultId) && !sourceOf.has(it.mergeResultId)) sourceOf.set(it.mergeResultId, it.itemId);
  const out = new Map<string, ChainInfo>();
  for (const it of items) {
    let cur = it.itemId, level = 1;
    const seen = new Set<string>([cur]);
    for (;;) {
      const prev = sourceOf.get(cur);
      if (!prev || seen.has(prev)) break;
      seen.add(prev); cur = prev; level += 1;
    }
    out.set(it.itemId, { chainId: cur, level });
  }
  return out;
}

/** 链 → 按等级排好的物品；末组「无合成关系」= 单个物品、既不合成也没被合成的 */
export function groupByChain(items: readonly MergeItem[]): Array<{ chainId: string; items: MergeItem[]; linked: boolean }> {
  const chains = deriveChains(items);
  const groups = new Map<string, MergeItem[]>();
  for (const it of items) {
    const c = chains.get(it.itemId)!.chainId;
    const list = groups.get(c) ?? []; list.push(it); groups.set(c, list);
  }
  const linked: Array<{ chainId: string; items: MergeItem[]; linked: boolean }> = [], loose: MergeItem[] = [];
  for (const [chainId, list] of groups) {
    list.sort((a, b) => chains.get(a.itemId)!.level - chains.get(b.itemId)!.level);
    if (list.length === 1 && !list[0].mergeResultId) loose.push(list[0]);
    else linked.push({ chainId, items: list, linked: true });
  }
  linked.sort((a, b) => a.chainId.localeCompare(b.chainId));
  if (loose.length) linked.push({ chainId: "", items: loose.sort((a, b) => a.itemId.localeCompare(b.itemId)), linked: false });
  return linked;
}

/** 生成器页的分组：散件各自算一条只有 Lv1 的链（没有合成关系的物品也能当生成器，服务端不拦），按链 id 排序 */
export function generatorGroups(items: readonly MergeItem[]): Array<{ chainId: string; items: MergeItem[] }> {
  return groupByChain(items)
    .flatMap((g) => (g.linked ? [{ chainId: g.chainId, items: g.items }] : g.items.map((it) => ({ chainId: it.itemId, items: [it] }))))
    .sort((a, b) => a.chainId.localeCompare(b.chainId));
}

export const weightToText = (w: number) => (w / 100).toFixed(2).replace(/\.?0+$/, "");
export const weightFromText = (s: string): number | null => {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

/** 物品被谁引用（编辑弹窗末尾的只读表；发布校验会拦引用失效，这里只是让运营看得见） */
export function itemReferences(b: MergeBlock, itemId: string): Array<{ where: "generator" | "order" | "board" | "intimacy"; by: string }> {
  const out: Array<{ where: "generator" | "order" | "board" | "intimacy"; by: string }> = [];
  for (const it of b.items) if (it.produce?.drops.some((d) => d.itemDefId === itemId)) out.push({ where: "generator", by: it.itemId });
  for (const t of b.orderTemplates) if (t.requirements.some((r) => r.itemDefId === itemId)) out.push({ where: "order", by: t.templateId });
  // 订单配置文档（v0.23）：物品奖励组、树订单的需求与物品奖励
  for (const g of b.orders?.rewardGroups ?? []) if (g.payout.kind === "item" && g.payout.itemCode === itemId) out.push({ where: "order", by: g.code });
  for (const tr of b.orders?.trees ?? []) for (const o of tr.orders) {
    if (o.objectives.some((x) => x.itemCode === itemId) || o.reward.items.some((x) => x.itemCode === itemId)) out.push({ where: "order", by: `${tr.id}/${o.orderId}` });
  }
  if (b.board?.initialItems.some((x) => x.itemDefId === itemId)) out.push({ where: "board", by: `slot ${b.board.initialItems.filter((x) => x.itemDefId === itemId).map((x) => x.slot).join(",")}` });
  for (const r of b.intimacyRewards) if (r.items.some((x) => x.itemDefId === itemId)) out.push({ where: "intimacy", by: r.ruleId });
  return out;
}

/** NPC 被哪些车道槽、树订单当角色（删除按钮置灰的依据；旧订单模板的引用也算，过渡期还在） */
export function npcReferences(b: MergeBlock, npcId: string): string[] {
  const out: string[] = [];
  b.orders?.lanes.forEach((l) => l.slots.forEach((s, j) => { if (s.characters.includes(npcId)) out.push(`${l.laneId}/${j + 1}`); }));
  for (const tr of b.orders?.trees ?? []) for (const o of tr.orders) if (o.character === npcId) out.push(`${tr.id}/${o.orderId}`);
  for (const t of b.orderTemplates) if (t.npcId === npcId) out.push(t.templateId);
  return out;
}

/** 合成链 id（按合成关系推导，链头 id）；producer = 链上任一级有生产能力，引擎把它当生成器链 */
export function chainSummaries(items: readonly MergeItem[]): Array<{ chainId: string; producer: boolean; length: number }> {
  const chains = deriveChains(items);
  const byChain = new Map<string, { producer: boolean; length: number }>();
  for (const it of items) {
    const c = chains.get(it.itemId) ?? { chainId: it.itemId, level: 1 };
    const cur = byChain.get(c.chainId) ?? { producer: false, length: 0 };
    byChain.set(c.chainId, { producer: cur.producer || !!it.produce, length: Math.max(cur.length, c.level) });
  }
  return [...byChain].map(([chainId, v]) => ({ chainId, ...v })).sort((a, b) => a.chainId.localeCompare(b.chainId));
}

// ── 生成器仓库（规格 v0.26 3.1 / 3.2 / 6.6 / 11 节）──

export type ChainTop = { chainId: string; top: MergeItem; level: number };

/**
 * 每条链的链顶：链上 merge 为空的那一级（规格 3.1「链顶」），等级按合成关系推。
 * 合成关系成环、链上找不到末级的链不出（发布闸会先报合成关系）
 */
export function chainTops(items: readonly MergeItem[]): Map<string, ChainTop> {
  const chains = deriveChains(items);
  const out = new Map<string, ChainTop>();
  for (const it of items) {
    if (it.mergeResultId) continue;
    const c = chains.get(it.itemId) ?? { chainId: it.itemId, level: 1 };
    out.set(c.chainId, { chainId: c.chainId, top: it, level: c.level });
  }
  return out;
}

/**
 * 生成器仓库能配的链：链顶能生产。和发布闸同一个口径（服务端 merge-validate 拦「链顶没有 produce」），
 * 不用 chainSummaries 的「链上任一级能生产」，那样下拉里会出现发布必拒的链。按链首 id 排
 */
export function producerWarehouseChains(items: readonly MergeItem[]): ChainTop[] {
  return [...chainTops(items).values()].filter((c) => !!c.top.produce).sort((a, b) => a.chainId.localeCompare(b.chainId));
}

/** 全部开齐要几级：解锁等级和每格开格等级取最大；还没有格是 null */
export function producerWarehouseAllOpenLevel(pw: MergeProducerWarehouse): number | null {
  return pw.slots.length ? Math.max(pw.unlockIntimacyLevel, ...pw.slots.map((s) => s.unlockIntimacyLevel)) : null;
}

/** 格子表上移 / 下移一格（顺序就是面板顺序）；越界原样返回 */
export function moveProducerSlot(pw: MergeProducerWarehouse, i: number, d: -1 | 1): MergeProducerWarehouse {
  const j = i + d;
  if (i < 0 || j < 0 || i >= pw.slots.length || j >= pw.slots.length) return pw;
  const slots = [...pw.slots];
  [slots[i], slots[j]] = [slots[j], slots[i]];
  return { ...pw, slots };
}

export type ProducerWarehouseRow = {
  /** 配置里的第几格（从 1 起）；配置里已删掉的格是 null */
  order: number | null;
  chainId: string;
  item: MergeSaveWarehouseItem | undefined;
  /** 配置里已删这格：有生成器照常能取，取空了客户端就不再画 */
  removed: boolean;
};

/**
 * 玩家面板的生成器仓库块（规格 11 节）：已开的格按配置顺序列；配置里已删掉但还存着生成器的格排最后、标出来；
 * 配置里删掉的空格不列（客户端也不画）。`cfgSlots` 是存档那一版配置的格子表，null = 那一版配置还没取到，
 * 就按存档里的顺序列、不判删没删
 */
export function producerWarehouseRows(
  state: { slots: ReadonlyArray<{ chainId: string; item?: MergeSaveWarehouseItem }> },
  cfgSlots: ReadonlyArray<{ chainId: string }> | null,
): ProducerWarehouseRow[] {
  if (cfgSlots == null) return state.slots.map((s) => ({ order: null, chainId: s.chainId, item: s.item, removed: false }));
  const at = new Map(cfgSlots.map((s, i) => [s.chainId, i] as const));
  const kept = state.slots.filter((s) => at.has(s.chainId)).sort((a, b) => at.get(a.chainId)! - at.get(b.chainId)!);
  const gone = state.slots.filter((s) => !at.has(s.chainId) && s.item != null);
  return [
    ...kept.map((s) => ({ order: at.get(s.chainId)! + 1, chainId: s.chainId, item: s.item, removed: false })),
    ...gone.map((s) => ({ order: null, chainId: s.chainId, item: s.item, removed: true })),
  ];
}

// ── 图鉴（规格 v0.25 3.1 / 3.2 / 11 节）──

/** 图鉴奖励数量合不合法：正的安全整数（与发布闸 items[<id>].dexReward.amount 同一条） */
export const dexAmountOk = (n: number | null | undefined): n is number => n != null && Number.isSafeInteger(n) && n > 0;

export type DexChain = { chainId: string; producer: boolean; items: MergeItem[] };

/**
 * 图鉴的卡（规格 3.1，和客户端同一口径）：一条链一张卡，生成器链（链上任一级有 produce）在前、其余在后，各自按链首 id 升序；
 * 卡内按等级。没有合成关系的物品各自成一张只有 Lv1 的卡（同生成器页的分组）
 */
export function dexChains(items: readonly MergeItem[]): DexChain[] {
  const groups = generatorGroups(items).map((g) => ({ ...g, producer: g.items.some((it) => !!it.produce) }));
  return [...groups.filter((g) => g.producer), ...groups.filter((g) => !g.producer)];
}

/** 图鉴页页顶的汇总：配了奖励的物品几件、全部领完合计多少体力 / 钻石。只算数量合法的格，填错的不进合计 */
export function dexSummary(items: readonly MergeItem[]): { count: number; energy: number; gems: number } {
  const out = { count: 0, energy: 0, gems: 0 };
  for (const it of items) {
    if (!it.dexRewardKind || !dexAmountOk(it.dexRewardAmount)) continue;
    out.count += 1;
    out[it.dexRewardKind] += it.dexRewardAmount;
  }
  return out;
}

/**
 * 图鉴页按整链写回（规格 11 节）：链上各级的奖励照格子（种类空了数量一起空，有种类时数量空了记 0、让发布闸报出来，
 * 不留只有一半的行），链首写系列名（一种语言都没填就是 null），链上其余物品的系列名一律清掉。
 * `chain` 按等级排，第一个是链首；不在链上的物品原样返回
 */
export function writeDexChain(
  items: readonly MergeItem[],
  chain: ReadonlyArray<Pick<MergeItem, "itemId" | "dexRewardKind" | "dexRewardAmount">>,
  series: I18nText | null,
): MergeItem[] {
  const at = new Map(chain.map((c, i) => [c.itemId, { c, head: i === 0 }]));
  return items.map((it) => {
    const hit = at.get(it.itemId);
    if (!hit) return it;
    const kind = hit.c.dexRewardKind;
    return {
      ...it, dexRewardKind: kind, dexRewardAmount: kind ? hit.c.dexRewardAmount ?? 0 : null,
      seriesNameI18n: hit.head && filledI18n(series) ? series : null,
    };
  });
}

// ── 图鉴分组 ──

/**
 * 分组的盘点，和发布闸（服务端 merge-validate 的 dexGroups 段）同一个口径：
 * 链的全集 = 图鉴的卡（每件物品都属于某条链，没有合成关系的自成一链），按图鉴的顺序；
 * `unassigned` 没分进任何组的链；`stale` 组里填的不是现在的链首（物品删了、链前面插了一级）；
 * `duplicated` 分进了不止一组（或同组写了两次）的链。没配分组时 unassigned 是全部链，页面自己决定提不提示
 */
export function dexGroupCoverage(items: readonly MergeItem[], groups: readonly MergeDexGroup[]): {
  chains: string[]; unassigned: string[]; stale: Set<string>; duplicated: Set<string>;
} {
  const chains = dexChains(items).map((c) => c.chainId);
  const heads = new Set(chains);
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  const stale = new Set<string>();
  for (const g of groups) for (const c of g.chainIds) {
    if (seen.has(c)) duplicated.add(c);
    seen.add(c);
    if (!heads.has(c)) stale.add(c);
  }
  return { chains, unassigned: chains.filter((c) => !seen.has(c)), stale, duplicated };
}

/** 新组的 id：`group_1` 往后找第一个没被占的（不区分大小写，和服务端主键的判重一致） */
export function newDexGroupId(groups: readonly MergeDexGroup[]): string {
  const taken = new Set(groups.map((g) => g.groupId.trim().toLowerCase()));
  for (let n = 1; ; n++) if (!taken.has(`group_${n}`)) return `group_${n}`;
}

/**
 * 组 id 的行内检查（组没有对话框，改完就随整块自动保存，坏 id 会让服务端整块 400、连累别的页）：
 * "bad" 不合 ID_RE；"dup" 和别的组撞了（不区分大小写、去首尾空白）
 */
export function dexGroupIdIssue(groups: readonly MergeDexGroup[], i: number): "bad" | "dup" | null {
  const id = groups[i]?.groupId ?? "";
  if (!ID_RE.test(id)) return "bad";
  const k = id.trim().toLowerCase();
  return groups.some((g, j) => j !== i && g.groupId.trim().toLowerCase() === k) ? "dup" : null;
}

/** 列表上移 / 下移一位（页签顺序、组里卡片顺序都用它）；越界原样返回 */
export function moveAt<T>(list: readonly T[], i: number, d: -1 | 1): readonly T[] {
  const j = i + d;
  if (i < 0 || j < 0 || i >= list.length || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

export type DexReward = { kind: DexRewardKind; amount: number };
export type DexPlayerRow = {
  chainId: string; itemId: string; level: number; unlocked: boolean;
  /** 奖励一栏：领过的以最近命令里记的实领为准，没记到的（或没领的）按配置 */
  reward: DexReward | null;
  /** null = 没解锁（状态空着） */
  status: "none" | "pending" | "claimed" | null;
};

/**
 * 玩家面板的图鉴块（规格 11 节）：按链列出配置里的每件物品。解锁 = discovered，已领 = dexClaimed；
 * 待领取 = 已解锁、配置有图鉴奖励、还没领（现算不存，配置里已经没有的物品不算）。
 * 已领合计：每件优先取最近命令里领它那一条记的实领，最近命令里查不到（记录只留最近几十条）的按配置补，配置里也没有的只计件数
 */
export function dexPlayerView(
  player: { discovered?: readonly string[]; dexClaimed?: readonly string[] },
  recent: readonly MergeSaveRecord["recent"][number][],
  items: readonly MergeItem[],
): { rows: DexPlayerRow[]; unlocked: number; total: number; pending: number; claimed: { count: number; energy: number; gems: number } } {
  const discovered = new Set(player.discovered ?? []);
  const claimedIds = [...new Set(player.dexClaimed ?? [])];
  const claimedSet = new Set(claimedIds);
  // 最近命令按时间先后排着，后面的覆盖前面的：同一件领过两次（存档重建后）取最新那次
  const actual = new Map<string, DexReward>();
  for (const c of [...recent].sort((a, b) => a.at - b.at)) if (c.dex && c.code === "0") actual.set(c.dex.itemDefId, { kind: c.dex.kind, amount: c.dex.amount });
  const configOf = (it: MergeItem | undefined): DexReward | null => (it?.dexRewardKind ? { kind: it.dexRewardKind, amount: it.dexRewardAmount ?? 0 } : null);
  const chains = deriveChains(items);
  const rows: DexPlayerRow[] = dexChains(items).flatMap((g) => g.items.map((it): DexPlayerRow => {
    const unlocked = discovered.has(it.itemId), claimed = claimedSet.has(it.itemId), cfg = configOf(it);
    return {
      chainId: g.chainId, itemId: it.itemId, level: chains.get(it.itemId)?.level ?? 1, unlocked,
      reward: claimed ? actual.get(it.itemId) ?? cfg : cfg,
      status: !unlocked ? null : claimed ? "claimed" : cfg ? "pending" : "none",
    };
  }));
  const byId = new Map(items.map((it) => [it.itemId, it]));
  const claimed = { count: claimedIds.length, energy: 0, gems: 0 };
  for (const id of claimedIds) {
    const r = actual.get(id) ?? configOf(byId.get(id));
    if (r) claimed[r.kind] += r.amount;
  }
  return {
    rows, unlocked: rows.filter((r) => r.unlocked).length, total: rows.length,
    pending: rows.filter((r) => r.status === "pending").length, claimed,
  };
}

export const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export type MergeSection = "items" | "generators" | "board" | "orders" | "energy" | "warehouse" | "dex" | "intimacy";
/** 八个菜单项共用一份块状态，按页面 id 渲染不同画板 */
export const MERGE_PAGE_SECTION: Record<string, MergeSection> = {
  "merge-item": "items", "merge-generator": "generators", "merge-board": "board", "merge-order": "orders", "merge-energy": "energy",
  "merge-warehouse": "warehouse", "merge-dex": "dex", "merge-intimacy-reward": "intimacy",
};
export const MERGE_PAGE_IDS = Object.keys(MERGE_PAGE_SECTION);

// ── 玩家面板用的存档形状（src/game/merge-do-core.ts MergeDoRecord 的只读视图，字段按面板需要列） ──
export type MergeSaveItem = { instanceId: string; itemDefId: string; slot: number; createdAt: number; seed: string; producer: { charges: number | null; lifetimeProduced: number; nextChargeAt: number | null } | null; locked?: true };
/** 两个仓库里存的物品：棋盘实例去掉格号和锁定 */
export type MergeSaveWarehouseItem = Omit<MergeSaveItem, "slot" | "locked">;
export type MergeSaveOrder = {
  orderId: string; kind: "lane" | "tree" | "cleanup"; lane?: "easy" | "medium" | "hard"; slot?: number; treeId?: string; treeOrderId?: string;
  character: string; requirements: Array<{ itemDefId: string; quantity: number }>; reward: { fragment: number; items: Array<{ itemDefId: string; quantity: number }> };
  score: number; isFallback?: true; configVersion: string; issuedAt: number;
};
export type MergeSaveBoard = {
  idolId: string; intimacy: { level: number; maxMultiplier: number; readAt: number };
  items: MergeSaveItem[]; orders: MergeSaveOrder[];
  /** 键 `${lane}:${slot}`，该槽累计完成次数 */
  laneDone: Record<string, number>;
  treeOpened: string[]; treeDone: string[];
  /** 还没揭开的格（棋盘解锁）；揭完了就没有这个键 */
  hiddenSlots?: number[];
  /** 仓库：解锁之后才有这个键；存的物品按存入先后排 */
  warehouse?: { baseSlots: number; boughtSlots: number; items: MergeSaveWarehouseItem[] };
  /** 生成器仓库（v0.26）：解锁之后才有这个键；格按开的先后排、开过不收回，空格没有 item 键 */
  producerWarehouse?: { slots: Array<{ chainId: string; item?: MergeSaveWarehouseItem }> };
};
export type MergeSaveRecord = {
  rebuildNo: number;
  player: {
    configVersion: string; revision: number; settledAt: number; energy: { amount: number; nextRecoverAt: number | null }; rewardTotals: { fragment: string; gems: string }; boards: Record<string, MergeSaveBoard>; orderSeq: number;
    /** 见过的物品 = 图鉴解锁（v0.20 起；更老的存档副本可能没有这个键） */
    discovered?: string[];
    /** 领过图鉴奖励的物品（v0.25）；一件都没领过就没有这个键 */
    dexClaimed?: string[];
  } | null;
  latestSessionNo: number | null;
  lastAckedSeq: number;
  credits: Record<string, { creditId: string; commandId: string; rewardIndex: number; asset: "fragment" | "gems"; amount: string; status: "pending" | "credited" | "blocked"; createdAt: number; updatedAt: number }>;
  rewardUnits: Record<string, { unitId: string; grantId: string; idolId: string; ruleId: string; thresholdLevel: number; itemDefId: string; configVersion: string; seed: string; claimNotBefore: number; status: "pending" | "claimed"; instanceId: string | null }>;
  grantedLevels: Record<string, number>;
  intimacyEventReceipts: Record<string, number>;
  /** dex：领图鉴奖励成功时记的领了哪件、实领多少（v0.25）；别的命令没有这个键 */
  recent: Array<{ commandId: string; seq: number; boardId: string; type: string; at: number; code: string; message: string; dex?: { itemDefId: string; kind: DexRewardKind; amount: number } }>;
  /** 运营发放与删除（设计仓《合成与订单-运营发放与删除设计》）；之前的存档没有这几个键 */
  mailScanFrom?: number | null;
  mailGrantReceipts?: Record<string, { sentAt: number; state: "done" } | { sentAt: number; state: "void" } | { sentAt: number; state: "open"; targets: Record<string, number[]>; done: string[] }>;
  removalCursor?: number;
  appliedRemovals?: Array<{ seq: number; idolId: string | null; items: string[]; createdAtMs: number }>;
};
/** 买仓库格的记录（merge_warehouse_purchases，src/game/merge-warehouse-charge.ts）；老服务端没带这个键 */
export type MergeWarehousePurchaseRow = { purchaseId: string; idolId: string; slotNo: number; price: number; status: "charging" | "done" | "void"; createdAtMs: number; settledAtMs: number | null };
export type MergePlayerSaveView = { found: false } | { found: true; rebuildNo: number; revision: number; updatedAtMs: number; record: MergeSaveRecord; warehousePurchases?: MergeWarehousePurchaseRow[] };

/** 运营删除的一条（GET /admin/api/merge/removals）。只追加、不能撤回 */
export type MergeRemovalRow = {
  seq: number;
  /** null = 全服 */
  user_id_hex: string | null;
  /** null = 所有 idol */
  idol_id: string | null;
  items: string[];
  reason: string;
  operator: string;
  created_at_ms: number;
};

/** 全服删除的二次确认：运营手打的物品 id 和选中的完全一致才放行（顺序、逗号空格不论） */
export function removalTypedMatches(typed: string, items: readonly string[]): boolean {
  const got = typed.split(/[\s,，、]+/).filter(Boolean);
  return got.length === items.length && new Set(got).size === got.length && got.every((x) => items.includes(x));
}
